import { useSuspendableResource } from 'useSuspendableResource';
import { useMemo } from 'react';
import { type StudentDashboardContentResponse } from 'StudentDashboard';
import { camelCaseStream, type Stream } from 'Lessons';
import { playlistIguanaObjectToCamelCase, type Playlist } from 'Playlists';
import { useLearnerContentCache } from './useLearnerContentCache';

// Right now it's implied that this hook is for the current User's student dashboard content.
// That implication only exists because LearnerContentCache.ensureStudentDashboard uses Angular.JS's currentUser.
// When we get rid of LearnerContentCache entirely, we'll want to `useCurrentUser` here.
export function useStudentDashboardContent(): StudentDashboardContentResponse {
    const LearnerContentCache = useLearnerContentCache();

    const { result } = useSuspendableResource(LearnerContentCache.suspendableResource);

    const lessonStreams = useMemo(
        () => result[0].lesson_streams.map(stream => camelCaseStream(stream.asJson())),
        [result],
    );
    const availablePlaylists = useMemo(
        () => result[0].available_playlists.map(playlist => playlistIguanaObjectToCamelCase(playlist)!),
        [result],
    );

    const studentDashboardContent = useMemo(
        () => ({
            lessonStreams,
            availablePlaylists,
            pastDueProjects: result[0].past_due_projects,
            lessonStreamsByLocalePackId: lessonStreams.reduce((acc, stream) => {
                acc[stream.localePack.id] = stream;
                return acc;
            }, {} as Record<string, Stream>),
            availablePlaylistsByLocalePackId: availablePlaylists.reduce((acc, playlist) => {
                acc[playlist.localePackId] = playlist;
                return acc;
            }, {} as Record<string, Playlist>),
        }),
        [result, lessonStreams, availablePlaylists],
    );

    return studentDashboardContent;
}
